/**
 * pages/Clients.tsx
 *
 * CRM client list. Each row navigates to the client detail page (/clients/:id).
 * Shows an unread indicator (blue dot overlay on avatar) when a client has
 * completed forms not yet viewed.
 * Updated Mar 2026: table columns changed to Nom, Prénom, Email (first_name/last_name).
 *   Search now covers first_name, last_name, and email.
 * Updated Mar 2026: removed redundant "Nouveau" badge (blue dot on avatar is sufficient);
 *   removed "Formulaires" column; replaced "Dernier complété" with "Date de création";
 *   added avatar (initials) column with blue dot overlay for unread indicator.
 * Updated Mar 2026: filteredClients short-circuits on empty query (returns true) so
 *   auto-created clients with all-null fields are always shown when no search is active.
 * Updated Apr 2026: ClientAvatar extracted to shared component src/components/ui/ClientAvatar.tsx.
 * Updated Apr 2026: Nom column no longer falls back to client.name to prevent first_name
 *   appearing in the Nom column when last_name is NULL.
 * Updated Apr 2026: Added PendingMatchBanner and ClientMatchResolverModal for client
 *   matching resolution workflow; filters out soft-deleted clients.
 * Updated Apr 2026: Unified "Client" column using clientDisplay helpers; added company type
 *   badge (Building2 icon); extended search to include company_name and
 *   company_registration_number; ClientAvatar now receives clientType for adaptive rendering.
 * Updated Apr 2026: added pending_attachment_count to stats (used for internal logic).
 * Updated Jun 2026: removed "À classer" amber badge (blue unread dot is sufficient indicator).
 * Updated Jun 2026: uses useEffectiveUser so that in impersonation mode the admin
 * sees the target user's clients list.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';
import { formatDateNullable } from '../utils/dateUtils';
import { getClientDisplayName } from '../utils/clientDisplay';
import AppLayout from '../components/layout/AppLayout';
import Card from '../components/ui/Card';
import ClientAvatar from '../components/ui/ClientAvatar';
import Spinner from '../components/ui/Spinner';
import PendingMatchBanner from '../components/clients/PendingMatchBanner';
import ClientMatchResolverModal from '../components/clients/ClientMatchResolverModal';
import { useClientMatching } from '../hooks/useClientMatching';
import { Search, Users, ChevronRight } from 'lucide-react';
import type { Database } from '../types/database';

type Client = Database['public']['Tables']['clients']['Row'];

interface ClientWithStats extends Client {
  request_count: number | null;
  completed_count: number | null;
  has_unread: boolean | null;
  last_completed_date: string | null;
  has_pending_attachment: boolean | null;
}

export default function Clients() {
  const { user } = useAuth();
  const { effectiveUserId, isImpersonating } = useEffectiveUser();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMatchResolver, setShowMatchResolver] = useState(false);

  const {
    pendingRequests,
    confirming,
    findCandidates,
    confirmMatch,
    createNewClient,
    dismiss,
  } = useClientMatching({ userId: isImpersonating ? undefined : user?.id, showToast });

  useEffect(() => {
    fetchClients();
  }, [effectiveUserId]);

  const fetchClients = async () => {
    if (!effectiveUserId) return;

    try {
      const { data: clientsData, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', effectiveUserId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const { data: statsData, error: statsError } = await supabase.rpc('get_clients_stats', {
        p_user_id: effectiveUserId,
      });

      if (statsError) {
        console.error('Error fetching client stats:', statsError);
        captureError(statsError, { page: 'Clients', action: 'get_clients_stats', userId: effectiveUserId });
        showToast('Impossible de charger les statistiques clients', 'error');
      }

      const statsMap = statsError
        ? null
        : new Map(
            (statsData || []).map((s: {
              client_id: string;
              total_count: number;
              completed_count: number;
              unread_count: number;
              last_completed_at: string | null;
              pending_attachment_count: number;
            }) => [s.client_id, s])
          );

      const clientsWithStats = (clientsData || []).map((client) => {
        if (!statsMap) {
          return {
            ...client,
            request_count: null,
            completed_count: null,
            has_unread: null,
            last_completed_date: null,
            has_pending_attachment: null,
          };
        }
        const s = statsMap.get(client.id);
        return {
          ...client,
          request_count: Number(s?.total_count ?? 0),
          completed_count: Number(s?.completed_count ?? 0),
          has_unread: Number(s?.unread_count ?? 0) > 0,
          last_completed_date: s?.last_completed_at ?? null,
          has_pending_attachment: Number(s?.pending_attachment_count ?? 0) > 0,
        };
      });

      setClients(clientsWithStats);
    } catch (error) {
      console.error('Error fetching clients:', error);
      captureError(error, { page: 'Clients', action: 'fetchClients', userId: effectiveUserId });
      showToast('Erreur lors du chargement des clients.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMatch = async (requestId: string, clientId: string): Promise<boolean> => {
    const ok = await confirmMatch(requestId, clientId);
    if (ok) await fetchClients();
    return ok;
  };

  const handleCreateNewClient = async (requestId: string): Promise<boolean> => {
    const ok = await createNewClient(requestId);
    if (ok) await fetchClients();
    return ok;
  };

  const filteredClients = clients.filter((client) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      client.last_name?.toLowerCase().includes(query) ||
      client.first_name?.toLowerCase().includes(query) ||
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.company_name?.toLowerCase().includes(query) ||
      client.company_registration_number?.toLowerCase().includes(query)
    );
  });

  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Qollect', path: '/dashboard' },
        { label: 'Clients' },
      ]}
    >
      {pendingRequests.length > 0 && (
        <div className="mb-4">
          <PendingMatchBanner
            count={pendingRequests.length}
            onClick={() => setShowMatchResolver(true)}
          />
        </div>
      )}

      {clients.length > 0 && (
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white shadow-soft"
            />
          </div>
        </div>
      )}

      {loading ? (
        <Card>
          <div className="text-center py-16">
            <div className="text-primary-600 mb-4 flex justify-center">
              <Spinner size="md" />
            </div>
            <p className="text-neutral-500">Chargement...</p>
          </div>
        </Card>
      ) : clients.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-50 rounded-3xl mb-5">
              <Users className="w-10 h-10 text-primary-600" />
            </div>
            <p className="text-neutral-600 mb-2 text-lg font-semibold">Aucun client pour le moment</p>
            <p className="text-sm text-neutral-400">
              Vos clients apparaîtront ici quand ils auront complété un formulaire
            </p>
          </div>
        </Card>
      ) : filteredClients.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <p className="text-neutral-500 text-lg">Aucun client ne correspond à votre recherche</p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden md:table-cell">
                    Adresse email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden lg:table-cell">
                    Client depuis
                  </th>
                  <th className="w-10 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredClients.map((client) => {
                  const displayName = getClientDisplayName(client);

                  return (
                    <tr
                      key={client.id}
                      onClick={() => navigate(`/clients/${client.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/clients/${client.id}`);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      className="hover:bg-neutral-50 transition-colors cursor-pointer group focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 focus-visible:outline-offset-[-2px]"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <ClientAvatar
                            displayName={displayName}
                            clientType={client.client_type}
                            hasUnread={client.has_unread}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-neutral-900 truncate">
                                {displayName || <span className="text-neutral-400">Sans nom</span>}
                              </span>
                            </div>
                            {/* Email shown on mobile where the email column is hidden */}
                            {client.email && (
                              <p className="text-xs text-neutral-500 truncate mt-0.5 md:hidden">
                                {client.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 hidden md:table-cell">
                        {client.email
                          ? <span className="truncate max-w-[220px] block">{client.email}</span>
                          : <span className="text-neutral-400">-</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 hidden lg:table-cell">
                        {formatDateNullable(client.created_at)}
                      </td>
                      <td className="px-4 py-4 text-neutral-300 group-hover:text-neutral-500 transition-colors">
                        <ChevronRight className="w-4 h-4" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <ClientMatchResolverModal
        isOpen={showMatchResolver}
        requests={pendingRequests}
        onClose={() => setShowMatchResolver(false)}
        onConfirmMatch={handleConfirmMatch}
        onCreateNew={handleCreateNewClient}
        onDismiss={dismiss}
        findCandidates={findCandidates}
        confirming={confirming}
      />
    </AppLayout>
  );
}
